import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";

const REPORT_KINDS = [
  { key: "assigned", label: "My Assigned" },
  { key: "pending", label: "Pending" },
  { key: "completed", label: "Completed" },
  { key: "parts_pending", label: "Parts Pending" },
  { key: "paid", label: "Paid" },
  { key: "unpaid", label: "Unpaid" },
  { key: "visits", label: "Visit History" },
];

export default function TechReports() {
  const [sp, setSp] = useSearchParams();
  const kind = sp.get("kind") || "assigned";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const setKind = (k) => setSp({ kind: k });

  useEffect(() => {
    setData(null); setErr(null);
    api.get(`/technician/reports/${kind}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [kind]);

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="tech-reports">
      <h1 className="font-heading text-2xl font-bold text-slate-900">My Reports</h1>
      <div className="flex gap-2 flex-wrap" data-testid="report-tabs">
        {REPORT_KINDS.map((r) => (
          <Button key={r.key} variant={kind === r.key ? "default" : "outline"} size="sm"
                  onClick={() => setKind(r.key)} data-testid={`tab-${r.key}`}
                  className={kind === r.key ? "bg-slate-900 text-white" : ""}>
            {r.label}
          </Button>
        ))}
      </div>

      {err && <Card><CardContent className="p-4 text-rose-700 text-sm">{String(err)}</CardContent></Card>}
      {!data && !err && <div className="text-sm text-slate-500">Loading…</div>}
      {data && (
        <div className="space-y-2" data-testid="report-rows">
          <div className="text-xs text-slate-600 font-semibold">{data.count} record{data.count === 1 ? "" : "s"}</div>
          {data.kind === "visits" ? (
            (data.rows || []).map((v, i) => (
              <Card key={i}><CardContent className="p-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono font-semibold">{v.complaint_number}</span>
                  <span className="text-slate-500">Visit #{v.visit_number}</span>
                </div>
                <div className="text-sm text-slate-900 mt-1">{v.customer_name}</div>
                <div className="text-xs text-slate-600 mt-0.5">{v.visit_date}{v.distance_km != null ? ` · ${v.distance_km} km` : ""}</div>
                {v.remarks && <div className="text-xs text-slate-700 mt-1">{v.remarks}</div>}
              </CardContent></Card>
            ))
          ) : (
            (data.rows || []).map((c) => (
              <Link key={c.id} to={`/complaints/${c.id}`} className="block" data-testid={`row-${c.complaint_number}`}>
                <Card className="hover:shadow"><CardContent className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono font-semibold text-slate-900">{c.complaint_number}</span>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="text-sm text-slate-900 mt-1">{c.customer_name}</div>
                  <div className="text-xs text-slate-600">{c.brand} · {c.product_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{c.phone_number_1} · {c.district_area || c.province}</div>
                </CardContent></Card>
              </Link>
            ))
          )}
          {data.count === 0 && (
            <Card><CardContent className="p-6 text-center text-slate-400 text-sm">No records</CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}
